import { gql } from '@apollo/client';
import { StudyActivityTypes, Subject } from '@mildang/graphql-artifact';
import { isProject } from '@shared/constants/projectTarget';
import { defineParams, qsStr, z, zPreset } from '@shared/utils/routeBuilder';
import { encodeShort } from '@shared/utils/uuid-short';
import { getApolloClient } from '@ui/utils/createApolloClient';

function encodeId(id: string) {
  return window.btoa(`id:${id}`);
}

type AppEnv = 'production' | 'stage' | 'development' | 'local';
export type Host = 'CMS' | 'LMS' | 'CRM' | 'STUDENT' | 'HUB';

const TITLE_SPLITTER = ' | ';

function titleSplitter(titles: string[]) {
  return titles.join(TITLE_SPLITTER);
}

function joinWithSpaces(strings) {
  return strings.join(' ');
}

const apolloClient = getApolloClient();

// NEXT_PUBLIC_APP_ENV 환경변수에 따라 각 프로젝트 URL 리턴
// production | stage | development
export const SERVICE_HOST = (
  {
    production: {
      CMS: 'https://cms.mildang.kr',
      LMS: 'https://lms.mildang.kr',
      CRM: 'https://crm.mildang.kr',
      //신규SA주소(legacy주소는 https://student.mildang.kr)
      STUDENT: 'https://study.mildang.kr',
      HUB: 'https://hub.mildang.kr',
    },
    stage: {
      CMS: 'https://stage.cms.mildang.kr',
      LMS: 'https://stage.lms.mildang.kr',
      CRM: 'https://stage.crm.mildang.kr',
      STUDENT: 'https://stage.student.mildang.kr',
      HUB: 'https://stage.hub.mildang.kr',
    },
    development: {
      CMS: 'https://dev.cms.mildang.kr',
      LMS: 'https://dev.lms.mildang.kr',
      CRM: 'https://dev.crm.mildang.kr',
      STUDENT: 'https://dev.student.mildang.kr',
      HUB: 'https://dev.hub.mildang.kr',
    },
    // TODO: local 주소 정해야 됨
    local: {
      CMS: 'https://cms.mildang.kr',
      LMS: 'https://lms.mildang.kr',
      CRM: 'https://crm.mildang.kr',
      //신규SA주소(legacy주소는 https://student.mildang.kr)
      STUDENT: 'https://study.mildang.kr',
      HUB: 'https://hub.mildang.kr',
    },
  } as Record<AppEnv, Record<Host, string>>
)[process.env.NEXT_PUBLIC_APP_ENV || 'local'] as Record<Host, string>;

export const buildVersionCheckJsonPath = () => '/api/version';

const routerBuilder = defineParams({
  '/auth/aidt': zPreset.none,
  '/auth/logout': zPreset.none,
  '/auth/qr/[token]': z.object({
    token: z.string(),
  }),
  '/auth/q/[token]/[fakeToken]': z.object({
    token: z.string(),
    fakeToken: z.string(),
  }),
  '/assessment/n/[reportId]': z.object({
    reportId: zPreset.id,
  }),
  '/tag': z
    .object({
      tab: z.string().default('ENGLISH'),
      offset: zPreset.offset.optional(),
      limit: zPreset.limit.optional(),
      searchFilter: zPreset.resultActivatedFilter.optional(),
    })
    .optional(),
  '/assessment-result': zPreset.none,
  '/knowledge-tags': z.object({
    knowledgeTagId: zPreset.id.optional(),
    subject: zPreset.type<Subject>().optional().default('ENGLISH'),
    offset: zPreset.offset.optional(),
    limit: zPreset.limit.optional(),
    searchFilter: zPreset.resultActivatedFilter.optional(),
  }),
  '/knowledge-maps': z.object({
    offset: zPreset.offset.optional(),
    limit: zPreset.limit.optional(),
    searchFilter: zPreset.resultActivatedFilter.optional(),
  }),
  '/knowledge-maps/create': zPreset.none,
  '/knowledge-maps/[knowledgeMapId]': z.object({
    knowledgeMapId: zPreset.id,
  }),
  '/knowledge-map': zPreset.none,
  '/search-text': zPreset.none,
  '/search-question': zPreset.none,
  '/grammar': zPreset.none,
  '/group': zPreset.none,
  '/meta-content/create': zPreset.none,
  '/block-content/create': zPreset.none,
  '/block-content/[metaContentId]/edit': z.object({
    metaContentId: zPreset.id,
    chapter: zPreset.id.optional(),
    module: zPreset.id.optional(),
    activity: zPreset.id.optional(),
  }),
  '/block-content': z
    .object({
      limit: zPreset.limit.optional(),
      offset: zPreset.offset.optional(),
      searchFilter: zPreset.resultActivatedFilter.optional(),
      orderBy: zPreset.orderBy.optional(),
    })
    .optional()
    .default({}),
  '/grammars': z
    .object({
      offset: zPreset.offset.optional(),
      limit: zPreset.limit.optional(),
      searchFilter: zPreset.resultActivatedFilter.optional(),
      orderBy: zPreset.orderBy.optional(),
    })
    .optional(),
  '/grammars/[grammarId]': z.object({
    grammarId: zPreset.id,
    offset: zPreset.offset.optional(),
    limit: zPreset.limit.optional(),
    searchFilter: zPreset.resultActivatedFilter.optional(),
    orderBy: zPreset.orderBy.optional(),
  }),
  '/monthly-report': zPreset.none,
  '/monthly-report/preset/[subject]/[screen]': z.object({
    subject: z.union([z.literal('english'), z.literal('math')]),
    type: z.union([
      z.literal('teacher-comment'),
      z.literal('opening-announcement'),
      z.literal('this-month'),
      z.literal('next-month'),
      z.literal('regular-exam'),
      z.literal('closing-announcement'),
      z.literal('attitude'),
      z.literal('assessment-evaluation'),
    ]),
    detail: z.string().optional(),
    rating: z.number().optional(),
  }),

  '/report': z.object({}),
  '/level-report/[reportId]': z.object({
    reportId: zPreset.id,
  }),
  '/word': z
    .object({
      offset: zPreset.offset.optional(),
      limit: zPreset.limit.optional(),
      searchFilter: zPreset.resultActivatedFilter.optional(),
      orderBy: zPreset.orderBy.optional(),
    })
    .optional(),
  '/word/[wordId]': z.object({
    wordId: zPreset.id,
    defaultTabValue: z
      .union([z.literal('meaning'), z.literal('sameWord'), z.literal('participle')])
      .optional(),
    offset: zPreset.offset.optional(),
    limit: zPreset.limit.optional(),
    searchFilter: zPreset.resultActivatedFilter.optional(),
    orderBy: zPreset.orderBy.optional(),
  }),
  '/weekly-report/[groupId]/[userId]': z.object({
    groupId: zPreset.id,
    userId: zPreset.id,
    offset: zPreset.offset.optional(),
    limit: zPreset.limit.optional(),
  }),
  '/weekly-reports/[reportId]/detail/[userId]/[dueDate]': z.object({
    reportId: zPreset.id,
    userId: zPreset.id,
    dueDate: z.string(),
  }),
  '/student-note/[studyActivityInstanceId]': z.object({
    studyActivityInstanceId: zPreset.id,
    studentName: z.string(),
    feedTitle: z.string(),
    curriculumTitle: z.string(),
    saTitle: z.string(),
  }),
  '/handwriting-note/[studyActivityInstanceId]': z.object({
    studyActivityInstanceId: zPreset.id,
    studentName: z.string(),
    feedTitle: z.string(),
    curriculumTitle: z.string(),
    saTitle: z.string(),
  }),
  '/experience/[metaContentModuleId]': z.object({
    metaContentModuleId: zPreset.id,
    studyUnitId: zPreset.id.optional(),
    studyActivityId: zPreset.id.optional(),
    path: z.string().optional(),
    publisherName: z.string().optional(),
    filter: z.array(zPreset.type<StudyActivityTypes>()).optional(),
  }),
  '/account-setting': zPreset.none,
  '/curriculum': z
    .object({
      limit: zPreset.limit.optional(),
      offset: zPreset.offset.optional(),
      searchFilter: zPreset.resultActivatedFilter.optional(),
      orderBy: zPreset.orderBy.optional(),
    })
    .optional()
    .default({}),
  '/users': z
    .object({
      tab: z.union([z.literal('basic'), z.literal('permission'), z.literal('group')]).optional(),
      offset: zPreset.offset.optional(),
      limit: zPreset.limit.optional(),
      searchFilter: zPreset.resultActivatedFilter.optional(),
      orderBy: zPreset.orderBy.optional(),
      userId: zPreset.id.optional(),
    })
    .optional(),
  '/calendar': z.object({
    groupId: zPreset.id.optional(),
    studentId: zPreset.id.optional(),
    openAnalysisDrawer: z.boolean().optional(),
  }),
  '/complete-study': z
    .object({
      offset: zPreset.offset.optional(),
      limit: zPreset.limit.optional(),
      searchFilter: zPreset.resultActivatedFilter.optional(),
      orderBy: zPreset.orderBy.optional(),
    })
    .optional(),
  '/complete-study/[groupId]/[userId]': z.object({
    groupId: zPreset.id,
    userId: zPreset.id,
    startDate: z.string().optional(),
    endDate: z.string().optional(),
    from: z.string().optional(),
  }),
  '/curriculum/[curriculumId]/preview': z.object({
    curriculumId: zPreset.id,
  }),
  '/curriculum/[curriculumId]/preview/[dailyCurriculumShortId]/[studyModuleShortId]/[studyUnitShortId]/[studyUnitActivityShortId]/[activityShortId]':
    z.object({
      curriculumId: zPreset.id,
      activityId: zPreset.id,
      studyUnitActivityId: zPreset.id,
      studyUnitId: zPreset.id,
      studyModuleId: zPreset.id,
      dailyCurriculumId: zPreset.id,
    }),
  '/curriculum/[curriculumId]/preview/[dailyCurriculumShortId]': z.object({
    curriculumId: zPreset.id,
    dailyCurriculumId: zPreset.id,
  }),
  '/curriculum/[curriculumId]/settings/curriculum-info': z.object({
    curriculumId: zPreset.id,
  }),
  '/curriculum/[curriculumId]/settings/linked-curriculums': z.object({
    curriculumId: zPreset.id,
    query: z
      .object({
        cursor: z.string().optional(),
        searchFilter: z
          .object({
            filterBy: z
              .object({
                keyword: z.string().optional(),
              })
              .optional(),
          })
          .optional(),
        orderBy: z.undefined().optional(),
      })
      .optional(),
  }),
  '/group/[groupId]/today-mission': z.object({
    groupId: zPreset.id,
    todayMissionDate: z.string().optional(),
  }),
  '/group/[groupId]/my-learning-status': z.object({
    groupId: zPreset.id,
  }),
  '/group/[groupId]/my-learning-status/[curriculumId]': z.object({
    groupId: zPreset.id,
    curriculumId: zPreset.id,
  }),
  '/group/[groupId]/class-plan': z.object({
    groupId: zPreset.id,
  }),
  '/group/[groupId]/class-plan/editing': z.object({
    groupId: zPreset.id,
  }),
  '/group/[groupId]/class-plan/knowledge-map': z.object({
    groupId: zPreset.id,
  }),
  '/group/[groupId]/class-plan/editing/[csmId]': z.object({
    groupId: zPreset.id,
    csmId: zPreset.id,
  }),
  '/group/[groupId]/class-plan/editing/[csmId]/customActivity/[caId]': z.object({
    groupId: zPreset.id,
    csmId: zPreset.id,
    caId: zPreset.id,
  }),
  '/group/[groupId]/regular-class': z.object({
    groupId: zPreset.id,
  }),
  '/group/[groupId]/regular-class/[smiId]/learning/[learningActivityId]': z.object({
    groupId: zPreset.id,
    smiId: zPreset.id,
    learningActivityId: zPreset.id,
    chatbot: zPreset.coerceBoolean.optional(),
  }),
  '/group/[groupId]/regular-class/[smiId]/summary/[suiId]': z.object({
    groupId: zPreset.id,
    smiId: zPreset.id,
    suiId: zPreset.id,
  }),
  '/group/[groupId]/curriculum': z.object({
    groupId: zPreset.id,
  }),
  '/group/[groupId]/feed': z.object({
    groupId: zPreset.id,
  }),
  '/group/[groupId]/modum': z.object({
    groupId: zPreset.id,
  }),
  '/group/[groupId]/detail': z.object({
    groupId: zPreset.id,
  }),
  '/group/[groupId]/detail/theme': z.object({
    groupId: zPreset.id,
  }),
  '/group/[groupId]/learning-plan': z.object({
    groupId: zPreset.id,
  }),
  '/group/[groupId]/setting-form/[replicaCurriculumId]': z.object({
    groupId: zPreset.id,
    groupReplicaCurriculumId: zPreset.id,
    isEdit: z.boolean().optional(),
  }),
  '/group/[groupId]/setting-form/[replicaCurriculumId]/wrong': z.object({
    groupId: zPreset.id,
    groupReplicaCurriculumId: zPreset.id,
  }),
  '/group/[groupId]/board': z.object({
    groupId: zPreset.id,
  }),
  '/group/[groupId]/board/create': z.object({
    groupId: zPreset.id,
  }),
  '/group/[groupId]/board/[postId]/edit': z.object({
    groupId: zPreset.id,
    postId: zPreset.id,
  }),
  '/group/[groupId]/board/[postId]': z.object({
    groupId: zPreset.id,
    postId: zPreset.id,
  }),
  '/group/[groupId]/class-monitor': z.object({
    groupId: zPreset.id,
  }),
  '/group/[groupId]/class-monitor/[csmId]/learning/[learningActivityId]': z.object({
    groupId: zPreset.id,
    csmId: zPreset.id,
    learningActivityId: zPreset.id,
  }),
  '/group/[groupId]/class-monitor/[csmId]/learning-root/[learningActivityId]': z.object({
    groupId: zPreset.id,
    csmId: zPreset.id,
    learningActivityId: zPreset.id,
    modumHandwriting: zPreset.coerceBoolean.optional(),
    modumId: zPreset.id.optional(),
  }),
  '/group/[groupId]/class-monitor/[csmId]/summary/[suiId]': z.object({
    groupId: zPreset.id,
    csmId: zPreset.id,
    suiId: zPreset.id,
  }),
  '/group/[groupId]/class-monitor/[csmId]/summary/[suiId]/unit-overview': z.object({
    groupId: zPreset.id,
    csmId: zPreset.id,
    suiId: zPreset.id,
  }),
  '/group/[groupId]/class-monitor/redirect/learning/[customActivityId]': z.object({
    groupId: zPreset.id,
    customActivityId: zPreset.id,
  }),
  '/group/[groupId]/class-monitor/redirect/summary/[csuId]/[studentId]': z.object({
    groupId: zPreset.id,
    csuId: zPreset.id,
    studentId: zPreset.id,
  }),
  '/group/[groupId]/dashboard': z.object({
    groupId: zPreset.id,
  }),
  '/group/[groupId]/chat-monitor': z.object({
    groupId: zPreset.id,
  }),
  '/chat-attachment/[channelId]': z.object({
    channelId: zPreset.id,
    imageFileId: z.string().optional(),
    bundleMessageId: z.string().optional(),
  }),
  '/group/[groupId]/calendar': z.object({
    groupId: zPreset.id,
  }),

  '/group/[groupId]/learning-plan-feed': z.object({
    groupId: zPreset.id,
  }),
  '/group/[groupId]/learning-plan-feed/[studentId]': z.object({
    groupId: zPreset.id,
    studentId: zPreset.id,
  }),
  '/group/[groupId]/learning-plan-feed/[studentId]/[learningPlanId]/plan-management': z.object({
    groupId: zPreset.id,
    studentId: zPreset.id,
    learningPlanId: zPreset.id,
  }),
  '/group/[groupId]/learning-plan-feed/[studentId]/[learningPlanId]/custom-knowledge-map': z.object({
    groupId: zPreset.id,
    studentId: zPreset.id,
    learningPlanId: zPreset.id,
  }),
  '/group/[groupId]/student-management': z.object({
    groupId: zPreset.id,
  }),
  '/group/[groupId]/student-management/[studentId]': z.object({
    groupId: zPreset.id,
    studentId: zPreset.id,
  }),
  '/group/[groupId]/student-management/[studentId]/detail/[curriculumId]': z.object({
    groupId: zPreset.id,
    studentId: zPreset.id,
    curriculumId: zPreset.id,
  }),
  '/group/[groupId]/study-report/[studentId]': z.object({
    groupId: zPreset.id,
    studentId: zPreset.id,
  }),
  '/group/[groupId]/study-report/all': z.object({
    groupId: zPreset.id,
  }),
  '/group/[groupId]/study-report/all/[customCurriculumId]': z.object({
    groupId: zPreset.id,
    customCurriculumId: zPreset.id,
  }),
  '/group/[groupId]/study-report/[studentId]/[customCurriculumId]': z.object({
    groupId: zPreset.id,
    studentId: zPreset.id,
    customCurriculumId: zPreset.id,
  }),
  '/group/[groupId]/student-study-report/[studentId]': z.object({
    groupId: zPreset.id,
    studentId: zPreset.id,
  }),

  '/group/[groupId]/student-study-report/my-report': z.object({
    groupId: zPreset.id,
  }),
  '/group/[groupId]/student-study-report/[studentId]/[customCurriculumId]': z.object({
    groupId: zPreset.id,
    studentId: zPreset.id,
    customCurriculumId: zPreset.id,
  }),
  '/import-study-activity': z.object({
    dailyCurriculumId: zPreset.id,
    studyModuleId: zPreset.id,
    studyUnitId: zPreset.id,
    tabType: z
      .union([z.literal('contents'), z.literal('activities'), z.literal('knowledgeTags')])
      .optional()
      .default('contents'),
  }),
  '/learning-plan-feed': zPreset.none,
  '/learning-plan-feed/[groupId]': z.object({
    groupId: zPreset.id,
  }),
  '/learning-plan-feed/[groupId]/[studentId]': z.object({
    groupId: zPreset.id,
    studentId: zPreset.id,
  }),
  '/learning-plan-feed/[groupId]/[studentId]/[learningPlanId]/plan-management': z.object({
    groupId: zPreset.id,
    studentId: zPreset.id,
    learningPlanId: zPreset.id,
  }),
  '/learning-plan-feed/[groupId]/[studentId]/[learningPlanId]/custom-knowledge-map': z.object({
    groupId: zPreset.id,
    studentId: zPreset.id,
    learningPlanId: zPreset.id,
  }),
  '/today-mission': z
    .object({
      todayMissionDate: z.string().optional(),
    })
    .optional(),

  '/study-module/[studyModuleInstanceId]/[unitId]/[activityId]/result': z
    .object({
      studyModuleInstanceId: zPreset.id,
      unitId: zPreset.id,
      shouldAnswerBeRevealed: z.boolean().optional(),
      pageType: z.union([z.literal('summary'), z.literal('learn'), z.literal('result'), z.literal('report')]),
      todayMissionDate: z.string().optional(),
    })
    .and(
      z.union([
        z.object({
          pageType: z.union([z.literal('summary'), z.literal('report')]),
          activityId: z.undefined().optional(),
        }),
        z.object({
          pageType: z.union([z.literal('learn'), z.literal('result')]),
          activityId: z.string(),
        }),
      ]),
    ),
  '/dashboard': z
    .object({
      dueDate: z.string().optional(),
      groupId: z.string().optional(),
      managerId: z.string().optional(),
      studentId: z.string().optional(),
    })
    .optional(),
  '/chat-monitor': z
    .object({
      dueDate: z.string().optional(),
      groupId: z.string().optional(),
      managerId: z.string().optional(),
      studentId: z.string().optional(),
    })
    .optional(),
  '/class-room': z
    .object({
      dueDate: z.string().optional(),
    })
    .optional(),
  '/class-room/all': z
    .object({
      dueDate: z.string().optional(),
    })
    .optional(),
  '/class-monitor': z.object({
    groupId: z.string().optional(),
    dueDate: z.string().optional(),
    rootStudyModuleInstanceId: z.string().optional(),
    rootStudyUnitInstanceId: z.string().optional(),
    rootStudyActivityInstanceId: z.string().optional(),
    saType: z.union([z.literal('normal'), z.literal('modum')]).optional(),
    isSummary: zPreset.coerceBoolean.optional(),
    feedId: z.string().optional(),
  }),
  '/my-note': zPreset.none,
  '/my-group/[groupId]': z.object({
    groupId: zPreset.id,
    offset: zPreset.offset.optional().default(0),
    limit: zPreset.limit.optional().default(20),
    searchFilter: zPreset.resultActivatedFilter.optional(),
    fullQueryString: z.string().optional(),
  }),
  '/group/[groupId]/auth-qr-print': z.object({
    groupId: zPreset.id,
  }),
  '/my-learning-status/[groupId]': z.object({
    groupId: zPreset.id,
  }),
  '/my-learning-status/[groupId]/detail/[curriculumId]': z.object({
    groupId: zPreset.id,
    curriculumId: zPreset.id,
  }),
  '/my-knowledge-status/[groupId]': z.object({
    groupId: zPreset.id,
  }),
  '/meta-content-module/[metaContentModuleId]/lecture': z.object({
    metaContentModuleId: zPreset.id,
    focusIndex: z.union([z.string(), z.number()]).optional(),
  }),
  '/meta-content-module/[metaContentModuleId]/study-activity/create': z.object({
    metaContentModuleId: zPreset.id,
    type: zPreset.type<StudyActivityTypes>().optional(),
  }),
  '/meta-content-module/[metaContentModuleId]/block-activity/create': z.object({
    metaContentModuleId: zPreset.id,
  }),
  '/curriculum/[curriculumId]/settings/dailycurriculum-setting/[dailyCurriculumId]': z.object({
    curriculumId: zPreset.id,
    dailyCurriculumId: zPreset.id,
    mcmLimit: zPreset.limit.optional(),
    mcmOffset: zPreset.offset.optional(),
    mcmOrderBy: zPreset.orderBy.optional(),
  }),
  '/meta-content/[metaContentId]/analyze-data': z.object({
    metaContentId: zPreset.id,
    analyzeLimit: zPreset.limit.optional(),
    analyzeOffset: zPreset.offset.optional(),
    analyzeOrderBy: zPreset.orderBy.optional(),
    // TODO: 이건 정의하기 어려움
    analyzeSearchFilter: z.any().optional(),
  }),
  '/meta-content-module/[metaContentModuleId]/block-activity/[activityId]': z.object({
    metaContentModuleId: zPreset.id,
    activityId: zPreset.id,
    queryStringObject: z.record(z.any()).optional(),
  }),
  '/meta-content-module/[metaContentModuleId]/block-activity/[activityId]/one': z.object({
    metaContentModuleId: zPreset.id,
    activityId: zPreset.id,
  }),
  '/meta-content-module/[metaContentModuleId]/study-activity/[studyActivityId]': z.object({
    metaContentModuleId: zPreset.id,
    studyActivityId: zPreset.id,
    queryStringObject: z.record(z.any()).optional(), // queryStringObject의 정확한 형태를 모르므로 일반 record로 처리
  }),
  '/meta-content': z
    .object({
      limit: zPreset.limit.optional(),
      offset: zPreset.offset.optional(),
      searchFilter: zPreset.resultActivatedFilter.optional(),
      orderBy: zPreset.orderBy.optional(),
    })
    .optional()
    .default({}),
  '/meta-content/[metaContentId]/all-meta-content-module/[metaChapterId]/[metaContentModuleId]': z.object({
    metaContentId: zPreset.id,
    metaChapterId: zPreset.id,
    metaContentModuleId: zPreset.id.optional(),
    saLimit: zPreset.limit.optional(),
    saOffset: zPreset.offset.optional(),
    saSearchFilter: zPreset.resultActivatedFilter.optional(),
  }),
  '/meta-content/[metaContentId]/all-meta-content-module/[metaChapterId]/[metaContentModuleId]/[tabName]':
    z.object({
      metaContentId: zPreset.id,
      metaChapterId: zPreset.id,
      metaContentModuleId: zPreset.id,
      tabName: z.union([z.literal('dashboard'), z.literal('sentence'), z.literal('word')]),
    }),
  '/meta-content/[metaContentId]/settings/chapter-setting/[metaChapterId]': z.object({
    metaContentId: zPreset.id,
    metaChapterId: zPreset.id,
    mcmLimit: zPreset.limit.optional(),
    mcmOffset: zPreset.offset.optional(),
    mcmOrderBy: zPreset.orderBy.optional(),
    saLimit: zPreset.limit.optional(),
    saOffset: zPreset.offset.optional(),
    saSearchFilter: zPreset.resultActivatedFilter.optional(),
    saOrderBy: zPreset.orderBy.optional(),
    mcmId: zPreset.id.optional(),
  }),
  '/student-info': z.object({
    dueDate: z.string(),
    managerId: z.string(),
    groupId: zPreset.id,
    studentId: zPreset.id,
    title: z.string(),
    feedId: z.string().optional(),
    smiId: z.string().optional(),
    unitId: z.string().optional(),
    saiId: z.string().optional(),
  }),
  '/chat': z.object({}),
  '/chat/send-bulk-message': z.object({}),
  '/curriculum/create': zPreset.none,
  '/study-sync/[groupId]/[customStudyModuleId]/[customStudyUnitId]/learning/[customActivityId]': z.object({
    groupId: zPreset.id,
    customStudyModuleId: zPreset.id,
    customStudyUnitId: zPreset.id,
    customActivityId: zPreset.id,
  }),
  '/presentation/[groupId]/[customStudyModuleId]/[customStudyUnitId]/[customActivityId]': z.object({
    groupId: zPreset.id,
    customStudyModuleId: zPreset.id,
    customStudyUnitId: zPreset.id,
    customActivityId: zPreset.id,
  }),

  '/student-management': zPreset.none,
  '/student-management/[groupId]/[studentId]': z.object({
    groupId: zPreset.id,
    studentId: zPreset.id,
  }),
  '/student-management/[groupId]/[studentId]/detail/[curriculumId]': z.object({
    groupId: zPreset.id,
    studentId: zPreset.id,
    curriculumId: zPreset.id,
  }),
  '/student-knowledge-status': zPreset.none,
  '/student-knowledge-status/[groupId]/[studentId]': z.object({
    groupId: zPreset.id,
    studentId: zPreset.id,
  }),

  '/meta-content/[metaContentId]/content-info': z.object({
    metaContentId: zPreset.id,
  }),
  '/meta-content/[metaContentId]/chapter-setting/[metaChapterId]': z.object({
    metaContentId: zPreset.id,
    metaChapterId: zPreset.id,
  }),
  '/meta-content/[metaContentId]/chapter-setting/[metaChapterId]/[metaContentModuleId]': z.object({
    metaContentId: zPreset.id,
    metaChapterId: zPreset.id,
    metaContentModuleId: zPreset.id,
  }),
  '/meta-content/[metaContentId]/chapter-setting/[metaChapterId]/[metaContentModuleId]/[tabValue]': z.object({
    metaContentId: zPreset.id,
    metaChapterId: zPreset.id,
    metaContentModuleId: zPreset.id,
    tabValue: z.union([z.literal('dashboard'), z.literal('sentence'), z.literal('word')]),
  }),
  '/group/[groupId]/assignments': z.object({
    groupId: zPreset.id,
    limit: zPreset.limit.optional(),
    offset: zPreset.offset.optional(),
  }),
  '/group/[groupId]/assignments/[assignmentId]': z.object({
    groupId: zPreset.id,
    assignmentId: zPreset.id,
  }),
  '/group/[groupId]/my-assignments': z.object({
    groupId: zPreset.id,
    limit: zPreset.limit.optional(),
    offset: zPreset.offset.optional(),
  }),
  '/group/[groupId]/assignment-detail/[smiId]/learning/[learningActivityId]/[kind]': z.object({
    groupId: zPreset.id,
    smiId: zPreset.id,
    learningActivityId: zPreset.id,
    kind: z.union([z.literal('assignment'), z.literal('self-study'), z.literal('incorrect-note')]),
  }),
  '/group/[groupId]/assignment-detail/[smiId]/[kind]/summary/[suiId]': z.object({
    groupId: zPreset.id,
    smiId: zPreset.id,
    suiId: zPreset.id,
    kind: z.union([z.literal('assignment'), z.literal('self-study'), z.literal('incorrect-note')]),
  }),
  '/group/[groupId]/self-study': z.object({
    groupId: zPreset.id,
  }),
  '/group/[groupId]/incorrect-answer-note': z.object({
    groupId: zPreset.id,
  }),
  '/study-analytics': z.object({}),
  '/login-fail': zPreset.none,
  '/students': zPreset.none,
  '/students/[studentId]': z.object({
    studentId: zPreset.id,
  }),
  '/students/[studentId]/courses': z.object({
    studentId: zPreset.id,
  }),
  '/students/[studentId]/learning': z.object({
    studentId: zPreset.id,
  }),
  '/students/[studentId]/payment': z.object({
    studentId: zPreset.id,
  }),
  '/students/[studentId]/report': z.object({
    studentId: zPreset.id,
  }),
  '/students/[studentId]/records': z.object({
    studentId: zPreset.id,
  }),
  '/students/[studentId]/records/[studentRecordId]': z.object({
    studentId: zPreset.id,
    studentRecordId: zPreset.id,
  }),
  '/students/[studentId]/records/create': z.object({
    studentId: zPreset.id,
  }),
  '/students/[studentId]/records/[studentRecordId]/edit': z.object({
    studentId: zPreset.id,
    studentRecordId: zPreset.id,
  }),
  '/purchase-history': z.object({
    offset: zPreset.offset.optional(),
    limit: zPreset.limit.optional(),
    searchFilter: zPreset.resultActivatedFilter.optional(),
  }),
  '/purchase-history/[purchaseId]': z.object({
    purchaseId: zPreset.id,
  }),
  '/purchase-history/[purchaseId]/sales-counseling-record/create': z.object({
    purchaseId: zPreset.id,
  }),
  '/purchase-history/[purchaseId]/sales-counseling-record/[salesCounselingRecordId]': z.object({
    purchaseId: zPreset.id,
    salesCounselingRecordId: zPreset.id,
  }),
  '/purchase-history/[purchaseId]/sales-counseling-record/[salesCounselingRecordId]/edit': z.object({
    purchaseId: zPreset.id,
    salesCounselingRecordId: zPreset.id,
  }),
  '/group/[groupId]/group-attendance': z.object({
    groupId: zPreset.id,
  }),
  '/mildang-ai-logs/[studentId]': z.object({
    studentId: zPreset.id,
  }),
  '/school-managed': zPreset.none,
  '/[organizationId]/[groupId]/daily-dashboard': z.object({
    organizationId: zPreset.id,
    groupId: zPreset.id,
  }),
  '/[organizationId]/[groupId]/study-report/all': z.object({
    organizationId: zPreset.id,
    groupId: zPreset.id,
  }),
  '/[organizationId]/[groupId]/study-report/all/[customCurriculumId]': z.object({
    organizationId: zPreset.id,
    groupId: zPreset.id,
    customCurriculumId: zPreset.id,
  }),
  '/[organizationId]/[groupId]/study-report/[studentId]/[customCurriculumId]': z.object({
    organizationId: zPreset.id,
    groupId: zPreset.id,
    studentId: zPreset.id,
    customCurriculumId: zPreset.id,
  }),
  '/[organizationId]/[groupId]/class-plan': z.object({
    organizationId: zPreset.id,
    groupId: zPreset.id,
  }),
  '/group/[groupId]/homework-board': z.object({
    groupId: zPreset.id,
  }),
  '/group/[groupId]/homework-board/create': z.object({
    groupId: zPreset.id,
  }),
  '/group/[groupId]/homework-board/[postId]': z.object({
    groupId: zPreset.id,
    postId: zPreset.id,
  }),
  '/group/[groupId]/homework-board/[postId]/edit': z.object({
    groupId: zPreset.id,
    postId: zPreset.id,
  }),
  '/group/[groupId]/classroom-plan': z.object({
    groupId: zPreset.id,
  }),
  '/class-managed': zPreset.none,
  '/[organizationId]/[groupId]/setting': z.object({
    organizationId: zPreset.id,
    groupId: zPreset.id,
  }),
});

const routePathConfig = routerBuilder({
  '/auth/aidt': {
    href: () => '/auth/aidt',
    staticMetadata: {
      services: [],
    },
  },
  '/auth/logout': {
    href: () => '/auth/logout',
    staticMetadata: {
      services: ['b2c'],
    },
  },
  '/auth/qr/[token]': {
    href: ({ token }) => `/auth/qr/${token}`,
    staticMetadata: {
      services: ['b2c'],
    },
  },
  '/auth/q/[token]/[fakeToken]': {
    href: ({ token, fakeToken }) => `/auth/q/${token}/${fakeToken}`,
    staticMetadata: {
      services: ['b2c'],
    },
  },
  '/assessment/n/[reportId]': {
    href: ({ reportId }) => `/assessment/n/${encodeId(reportId)}`,
    staticMetadata: {
      services: ['b2c'],
    },
  },
  '/tag': {
    href: ({ ...query }) => `/tag${qsStr(query)}`,
    staticMetadata: {
      services: ['cms'],
      title: '태그 관리',
    },
  },
  '/knowledge-tags': {
    href: ({ ...query }) => `/knowledge-tags${qsStr(query)}`,
    staticMetadata: {
      services: ['cms'],
    },
  },
  '/knowledge-maps': {
    href: ({ ...query }) => `/knowledge-maps${qsStr(query)}`,
    staticMetadata: {
      services: ['cms'],
      title: '지식맵 관리',
    },
  },
  '/knowledge-maps/create': {
    href: () => '/knowledge-maps/create',
    staticMetadata: {
      services: ['cms'],
    },
  },
  '/knowledge-maps/[knowledgeMapId]': {
    href: ({ knowledgeMapId }) => `/knowledge-maps/${knowledgeMapId}`,
    staticMetadata: {
      services: ['cms'],
    },
  },
  '/assessment-result': {
    href: () => `/assessment-result`,
    staticMetadata: {
      services: ['lms'],
    },
  },
  '/knowledge-map': {
    href: () => `/knowledge-map`,
    staticMetadata: {
      services: ['lms'],
    },
  },
  '/search-text': {
    href: () => `/search-text`,
    staticMetadata: {
      services: ['cms'],
      title: '지문 검색기',
    },
  },
  '/search-question': {
    href: () => `/search-question`,
    staticMetadata: {
      services: ['cms'],
      title: '문제 검색기',
    },
  },
  '/grammar': {
    href: () => `/grammar`,
    staticMetadata: {
      services: ['b2c'],
    },
  },
  '/group': {
    href: () => `/group`,
    staticMetadata: {
      services: ['lms'],
      title: '그룹 관리',
    },
  },
  '/meta-content/create': {
    href: () => '/meta-content/create',
    staticMetadata: {
      services: ['cms'],
    },
  },
  '/block-content/create': {
    href: () => '/block-content/create',
    staticMetadata: {
      services: ['cms'],
    },
  },
  '/block-content/[metaContentId]/edit': {
    href: ({ metaContentId, ...query }) => `/block-content/${metaContentId}/edit${qsStr(query)}`,
    staticMetadata: {
      services: ['cms'],
    },
  },
  '/block-content': {
    href: ({ ...query }) => `/block-content${qsStr(query)}`,
    staticMetadata: {
      services: ['cms'],
      title: '블록 콘텐츠 관리',
    },
  },
  '/grammars': {
    href: (query) => `/grammars${qsStr(query)}`,
    staticMetadata: {
      services: ['cms'],
    },
  },
  '/grammars/[grammarId]': {
    href: ({ grammarId, ...query }) => `/grammars/${grammarId}${qsStr(query)}`,
    staticMetadata: {
      services: ['cms'],
    },
  },
  '/monthly-report': {
    href: () => `/monthly-report`,
    staticMetadata: {
      services: ['lms'],
    },
  },
  '/monthly-report/preset/[subject]/[screen]': {
    href: ({ subject, type, ...query }) => `/monthly-report/preset/${subject}/${type}${qsStr(query)}`,
    staticMetadata: {
      services: ['lms'],
    },
  },
  '/report': {
    href: ({ }) => `/report`,
    staticMetadata: {
      services: ['b2c'],
    },
  },
  '/level-report/[reportId]': {
    href: ({ reportId }) => `/level-report/${reportId}`,
    staticMetadata: {
      services: ['b2c'],
    },
  },
  '/word': {
    href: (query) => `/word${qsStr(query)}`,
    staticMetadata: {
      services: ['cms'],
      title: '단어 관리',
    },
  },
  '/word/[wordId]': {
    href: ({ wordId, ...query }) => `/word/${wordId}${qsStr(query)}`,
    staticMetadata: {
      services: ['cms'],
    },
  },
  '/weekly-report/[groupId]/[userId]': {
    href: ({ groupId, userId, ...query }) => `/weekly-report/${groupId}/${userId}${qsStr(query)}`,
    staticMetadata: {
      services: ['lms'],
    },
  },
  '/weekly-reports/[reportId]/detail/[userId]/[dueDate]': {
    href: ({ reportId, userId, dueDate }) => `/weekly-reports/${reportId}/detail/${userId}/${dueDate}`,
    staticMetadata: {
      services: ['b2c'],
    },
  },
  '/student-note/[studyActivityInstanceId]': {
    href: ({ studyActivityInstanceId, ...query }) =>
      `/student-note/${studyActivityInstanceId}${qsStr(query)}`,
    staticMetadata: {
      services: ['lms'],
    },
  },
  '/handwriting-note/[studyActivityInstanceId]': {
    href: ({ studyActivityInstanceId, ...query }) =>
      `/handwriting-note/${studyActivityInstanceId}${qsStr(query)}`,
    staticMetadata: {
      services: ['b2c'],
    },
  },
  '/experience/[metaContentModuleId]': {
    href: (params) =>
      `/experience/${params.metaContentModuleId}${qsStr({
        studyUnitId: params.studyUnitId,
        studyActivityId: params.studyActivityId,
        path: params.path,
        publisherName: params.publisherName,
        filter: params.filter,
      })}`,
    staticMetadata: {
      services: ['b2c'],
    },
  },
  '/account-setting': {
    href: () => `/account-setting`,
    staticMetadata: {
      services: [],
      title: '내 계정 관리',
    },
  },
  '/curriculum': {
    href: ({ ...query }) => {
      return `/curriculum${qsStr(query)}`;
    },
    staticMetadata: {
      services: ['cms'],
      title: '교재 관리',
    },
  },
  '/users': {
    href: (params) =>
      `/users${params
        ? qsStr({
          limit: params.limit ?? 10,
          offset: params.offset ?? 0,
          searchFilter: params.searchFilter,
          orderBy: params.orderBy,
          tab: params.tab,
          userId: params.userId,
        })
        : ''
      }`,
    staticMetadata: {
      services: ['crm'],
      title: '유저 관리',
    },
  },
  '/calendar': {
    href: (query) => `/calendar${qsStr(query)}`,
    staticMetadata: {
      services: ['lms'],
      title: '일정 관리',
    },
  },
  '/complete-study': {
    href: (query) => `/complete-study${qsStr(query)}`,
    staticMetadata: {
      services: ['lms'],
      title: '학습 이력',
    },
  },
  '/complete-study/[groupId]/[userId]': {
    href: ({ groupId, userId, ...query }) => `/complete-study/${groupId}/${userId}${qsStr(query)}`,
    staticMetadata: {
      services: ['lms'],
    },
  },

  '/curriculum/[curriculumId]/preview': {
    href: ({ curriculumId }) => `/curriculum/${curriculumId}/preview`,
    staticMetadata: {
      services: ['cms'],
    },
  },
  '/curriculum/[curriculumId]/preview/[dailyCurriculumShortId]': {
    href: ({ curriculumId, dailyCurriculumId }) =>
      `/curriculum/${curriculumId}/preview/${encodeShort(dailyCurriculumId)}`,
    staticMetadata: {
      services: ['cms'],
    },
  },

  '/curriculum/[curriculumId]/preview/[dailyCurriculumShortId]/[studyModuleShortId]/[studyUnitShortId]/[studyUnitActivityShortId]/[activityShortId]':
  {
    href: ({
      curriculumId,
      dailyCurriculumId,
      studyUnitId,
      studyModuleId,
      studyUnitActivityId,
      activityId,
    }) =>
      `/curriculum/${curriculumId}/preview/${encodeShort(dailyCurriculumId)}/${encodeShort(
        studyModuleId,
      )}/${encodeShort(studyUnitId)}/${encodeShort(studyUnitActivityId)}/${encodeShort(activityId)}`,

    staticMetadata: {
      services: ['cms'],
    },
  },

  '/curriculum/[curriculumId]/settings/curriculum-info': {
    href: ({ curriculumId }) => `/curriculum/${curriculumId}/settings/curriculum-info`,
    staticMetadata: {
      services: ['cms'],
    },
  },
  '/curriculum/[curriculumId]/settings/linked-curriculums': {
    href: ({ curriculumId, ...query }) =>
      `/curriculum/${curriculumId}/settings/linked-curriculums${qsStr(query)}`,
    staticMetadata: {
      services: ['cms'],
    },
  },
  '/group/[groupId]/class-plan': {
    href: ({ groupId, ...query }) => `/group/${groupId}/class-plan${qsStr(query)}`,
    staticMetadata: {
      services: ['lms'],
    },
  },
  '/group/[groupId]/class-plan/editing': {
    href: ({ groupId, ...query }) => `/group/${groupId}/class-plan/editing${qsStr(query)}`,
    staticMetadata: {
      services: ['lms'],
    },
  },
  '/group/[groupId]/class-plan/knowledge-map': {
    href: ({ groupId, ...query }) => `/group/${groupId}/class-plan/knowledge-map${qsStr(query)}`,
    staticMetadata: {
      services: ['lms'],
    },
  },
  '/group/[groupId]/class-plan/editing/[csmId]': {
    href: ({ groupId, csmId }) => `/group/${groupId}/class-plan/editing/${csmId}`,
    staticMetadata: {
      services: ['lms'],
    },
  },
  '/group/[groupId]/class-plan/editing/[csmId]/customActivity/[caId]': {
    href: ({ groupId, csmId, caId, ...query }) =>
      `/group/${groupId}/class-plan/editing/${csmId}/customActivity/${caId}${qsStr(query)}`,
    staticMetadata: {
      services: ['lms'],
    },
  },
  '/group/[groupId]/curriculum': {
    href: ({ groupId }) => `/group/${groupId}/curriculum`,
    staticMetadata: {
      services: ['lms'],
    },
    dynamicMetadata: async ({ groupId }) => {
      const result = await apolloClient.query({
        query: gql`
          query DynamicMetadata_groupCurriculum($groupId: ID!) {
            group(id: $groupId) {
              id
              name
            }
          }
        `,
        variables: { groupId },
      });

      return {
        title: titleSplitter([result?.data?.group?.name ?? '', '마이 클래스']),
      };
    },
  },
  '/group/[groupId]/feed': {
    href: ({ groupId }) => `/group/${groupId}/feed`,
    staticMetadata: {
      services: ['lms'],
    },
  },
  '/group/[groupId]/modum': {
    href: ({ groupId }) => `/group/${groupId}/modum`,
    staticMetadata: {
      services: ['lms'],
    },
  },
  '/group/[groupId]/detail': {
    href: ({ groupId }) => `/group/${groupId}/detail`,
    staticMetadata: {
      services: ['lms'],
    },
  },
  '/group/[groupId]/detail/theme': {
    href: ({ groupId }) => `/group/${groupId}/detail/theme`,
    staticMetadata: {
      services: ['lms'],
    },
  },
  '/group/[groupId]/learning-plan': {
    href: ({ groupId }) => `/group/${groupId}/learning-plan`,
    staticMetadata: {
      services: ['lms'],
    },
  },
  '/group/[groupId]/setting-form/[replicaCurriculumId]': {
    href: ({ groupId, groupReplicaCurriculumId, ...query }) =>
      `/group/${groupId}/setting-form/${groupReplicaCurriculumId}${qsStr(query)}`,
    staticMetadata: {
      services: ['lms'],
    },
  },
  '/group/[groupId]/setting-form/[replicaCurriculumId]/wrong': {
    href: ({ groupId, groupReplicaCurriculumId }) =>
      `/group/${groupId}/setting-form/${groupReplicaCurriculumId}/wrong`,
    staticMetadata: {
      services: ['lms'],
    },
  },
  '/import-study-activity': {
    href: (query) => `/import-study-activity${qsStr(query)}`,
  },
  '/learning-plan-feed': {
    href: () => `/learning-plan-feed`,
    staticMetadata: {
      services: ['lms'],
      title: '피드 및 플랜',
    },
  },
  '/learning-plan-feed/[groupId]': {
    href: ({ groupId }) => {
      if (isProject('schoolpt')) {
        return `/group/${groupId}/learning-plan-feed`;
      }
      return `/learning-plan-feed/${groupId}`;
    },
    staticMetadata: {
      services: ['lms'],
    },
  },
  '/learning-plan-feed/[groupId]/[studentId]': {
    href: ({ groupId, studentId }) => {
      if (isProject('schoolpt')) {
        return `/group/${groupId}/learning-plan-feed/${studentId}`;
      }
      return `/learning-plan-feed/${groupId}/${studentId}`;
    },
    staticMetadata: {
      services: ['lms'],
    },
  },
  '/learning-plan-feed/[groupId]/[studentId]/[learningPlanId]/plan-management': {
    href: ({ groupId, studentId, learningPlanId }) => {
      if (isProject('schoolpt')) {
        return `/group/${groupId}/learning-plan-feed/${studentId}/${learningPlanId}/plan-management`;
      }
      return `/learning-plan-feed/${groupId}/${studentId}/${learningPlanId}/plan-management`;
    },
    staticMetadata: {
      services: ['lms'],
    },
  },
  '/learning-plan-feed/[groupId]/[studentId]/[learningPlanId]/custom-knowledge-map': {
    href: ({ groupId, studentId, learningPlanId }) => {
      if (isProject('schoolpt')) {
        return `/group/${groupId}/learning-plan-feed/${studentId}/${learningPlanId}/custom-knowledge-map`;
      }
      return `/learning-plan-feed/${groupId}/${studentId}/${learningPlanId}/custom-knowledge-map`;
    },
    staticMetadata: {
      services: ['lms'],
    },
  },

  '/today-mission': {
    href: (query) => `/today-mission${qsStr(query)}`,
    staticMetadata: {
      services: ['b2c'],
      title: '오늘의 미션',
    },
  },
  '/study-module/[studyModuleInstanceId]/[unitId]/[activityId]/result': {
    href: (params) =>
      `/study-module/${params.studyModuleInstanceId}/${params.unitId}/${params.pageType === 'summary' || params.pageType === 'report' ? params.pageType : params.activityId
      }${params.pageType === 'result' ? '/result' : ''}${qsStr({
        todayMissionDate: params.todayMissionDate,
        shouldAnswerBeRevealed: params.shouldAnswerBeRevealed,
      })}`,
    staticMetadata: {
      services: ['b2c'],
    },
    dynamicMetadata: async ({ studyModuleInstanceId, unitId, activityId, pageType }) => {
      const isSummary = pageType === 'summary';

      const result = await apolloClient.query({
        query: gql`
          query DynamicMetadata_studyModule(
            $studyModuleInstanceId: ID!
            $unitId: ID!
            $activityId: ID!
            $skipStudyActivityQuery: Boolean!
          ) {
            studyModuleInstanceById(id: $studyModuleInstanceId) {
              id
              title
              curriculumTitle
            }
            studyUnitInstance(id: $unitId) {
              id
              title
              prefixTitle
              ...getSUIType_StudyUnitInstance @unmask
              studyActivityInstances @skip(if: $skipStudyActivityQuery) {
                id
                type
                title
              }
            }
            studyActivityInstanceById(id: $activityId) @skip(if: $skipStudyActivityQuery) {
              id
              type
              title
            }
          }

          fragment getSUIType_StudyUnitInstance on StudyUnitInstance {
            id
            isEstimative
            category
            viewDatas {
              __typename
            }
          }
        `,
        variables: {
          studyModuleInstanceId,
          unitId,
          activityId: isSummary ? '' : activityId,
          skipStudyActivityQuery: isSummary,
        },
      });

      const matchingActivityInstance = isSummary
        ? ''
        : result?.data?.studyUnitInstance?.studyActivityInstances?.find(
          (instance) => instance.id === activityId,
        );

      let activityType = '';

      let activityTitle = '';

      if (matchingActivityInstance?.type) {
        activityType = '';
      }

      if (isSummary && result?.data?.studyUnitInstance?.prefixTitle === '단어 시험') {
        activityTitle = '단어 시험';
      } else {
        activityTitle = '학습 요약';
      }

      if (!isSummary) {
        activityTitle = joinWithSpaces([activityType, matchingActivityInstance.title]);
      }

      const unitTitleWithPrefix = joinWithSpaces([
        result?.data?.studyUnitInstance?.prefixTitle,
        result?.data?.studyUnitInstance?.title,
      ]);

      const curriculumTitle = result?.data?.studyModuleInstanceById?.curriculumTitle ?? '';

      const title = result?.data?.studyModuleInstanceById?.title ?? '';

      return {
        title: titleSplitter([activityTitle, unitTitleWithPrefix ?? '', curriculumTitle, title, '학습하기']),
      };
    },
  },
  '/dashboard': {
    href: (query) => `/dashboard${qsStr(query)}`,
    staticMetadata: {
      services: ['lms'],
      title: '학생 모니터링',
    },
  },
  '/chat-monitor': {
    href: (query) => `/chat-monitor${qsStr(query)}`,
    staticMetadata: {
      services: ['lms'],
      title: '채팅 대시보드',
    },
  },
  '/my-note': {
    href: () => `/my-note`,
    staticMetadata: {
      services: ['b2c'],
      title: '나만의 노트',
    },
  },
  '/my-group/[groupId]': {
    href: (params) =>
      `/my-group/${params.groupId}/${params.fullQueryString
        ? `?${params.fullQueryString}`
        : qsStr({
          searchFilter: params.searchFilter,
          limit: params.limit,
          offset: params.offset,
        })
      }`,
    dynamicMetadata: async ({ groupId }) => {
      const result = await apolloClient.query({
        query: gql`
          query DynamicMetadata_myGroup($groupId: ID!) {
            group(id: $groupId) {
              id
              name
            }
          }
        `,
        variables: { groupId },
      });

      return {
        title: titleSplitter([result?.data?.group?.name ?? '', '피드 및 플랜']),
      };
    },
    staticMetadata: {
      services: ['b2c'],
    },
  },
  '/group/[groupId]/auth-qr-print': {
    href: (params) => `/group/${params.groupId}/auth-qr-print`,
    staticMetadata: {
      services: ['lms'],
    },
  },
  '/my-learning-status/[groupId]': {
    href: (params) => `/my-learning-status/${params.groupId}`,
    staticMetadata: {
      services: ['b2c'],
    },
    dynamicMetadata: async ({ groupId }) => {
      const result = await apolloClient.query({
        query: gql`
          query DynamicMetadata_myLearningStatus($groupId: ID!) {
            group(id: $groupId) {
              id
              name
            }
          }
        `,
        variables: { groupId },
      });

      return {
        title: titleSplitter([result?.data?.group?.name ?? '', '학습 대시보드']),
      };
    },
  },
  '/my-learning-status/[groupId]/detail/[curriculumId]': {
    href: (params) => `/my-learning-status/${params.groupId}/detail/${params.curriculumId}`,
    staticMetadata: {
      services: ['b2c'],
    },
  },
  '/my-knowledge-status/[groupId]': {
    href: (params) => `/my-knowledge-status/${params.groupId}`,
    staticMetadata: {
      services: ['b2c'],
    },
    dynamicMetadata: async ({ groupId }) => {
      const result = await apolloClient.query({
        query: gql`
          query DynamicMetadata_myKnowledgeStatus($groupId: ID!) {
            group(id: $groupId) {
              id
              name
            }
          }
        `,
        variables: { groupId },
      });

      return {
        title: titleSplitter([result?.data?.group?.name ?? '', '내 지식 현황']),
      };
    },
  },
  '/class-room': {
    href: () => `/class-room`,
  },
  '/class-room/all': {
    href: () => `/class-room/all`,
  },
  '/meta-content': {
    href: ({ ...query }) => `/meta-content${qsStr(query)}`,
    staticMetadata: {
      services: ['cms'],
      title: '학습 자료 관리',
    },
  },
  '/meta-content/[metaContentId]/all-meta-content-module/[metaChapterId]/[metaContentModuleId]': {
    href: ({ metaContentId, metaChapterId, metaContentModuleId, ...query }) =>
      `/meta-content/${metaContentId}/all-meta-content-module/${metaChapterId}${metaContentModuleId ? `/${metaContentModuleId}` : ''
      }${qsStr(query)}`,
    staticMetadata: {
      services: ['cms'],
    },
  },
  '/meta-content/[metaContentId]/all-meta-content-module/[metaChapterId]/[metaContentModuleId]/[tabName]': {
    href: (params) =>
      `/meta-content/${params.metaContentId}/all-meta-content-module/${params.metaChapterId}/${params.metaContentModuleId}/${params.tabName}`,
    staticMetadata: {
      services: ['cms'],
    },
  },
  '/meta-content/[metaContentId]/settings/chapter-setting/[metaChapterId]': {
    href: (params) =>
      `/meta-content/${params.metaContentId}/chapter-setting/${params.metaChapterId}${qsStr({
        mcmLimit: params.mcmLimit,
        mcmOffset: params.mcmOffset,
        mcmOrderBy: params.mcmOrderBy,
        saLimit: params.saLimit,
        saOffset: params.saOffset,
        saSearchFilter: params.saSearchFilter,
        saOrderBy: params.saOrderBy,
        mcmId: params.mcmId,
      })}`,
    staticMetadata: {
      services: ['cms'],
    },
  },
  '/meta-content/[metaContentId]/content-info': {
    href: (params) => `/meta-content/${params.metaContentId}/content-info`,
    staticMetadata: {
      services: ['cms'],
    },
  },
  '/meta-content/[metaContentId]/analyze-data': {
    href: ({ metaContentId, ...query }) => `/meta-content/${metaContentId}/analyze-data${qsStr(query)}`,
    staticMetadata: {
      services: ['cms'],
    },
  },
  '/meta-content-module/[metaContentModuleId]/study-activity/[studyActivityId]': {
    href: ({ metaContentModuleId, studyActivityId, ...query }) =>
      `/meta-content-module/${metaContentModuleId}/study-activity/${studyActivityId}${qsStr(
        query.queryStringObject,
      )}`,
    staticMetadata: {
      services: ['cms'],
    },
  },
  '/meta-content-module/[metaContentModuleId]/block-activity/[activityId]': {
    href: ({ metaContentModuleId, activityId, ...query }) =>
      `/meta-content-module/${metaContentModuleId}/block-activity/${qsStr({
        ...query.queryStringObject,
        activityId,
      })}`,
    staticMetadata: {
      services: ['cms'],
    },
  },
  '/meta-content-module/[metaContentModuleId]/block-activity/[activityId]/one': {
    href: ({ metaContentModuleId, activityId }) =>
      `/meta-content-module/${metaContentModuleId}/block-activity/${activityId}`,
    staticMetadata: {
      services: ['cms'],
    },
  },
  '/meta-content-module/[metaContentModuleId]/lecture': {
    href: ({ metaContentModuleId, ...query }) =>
      `/meta-content-module/${metaContentModuleId}/lecture${qsStr(query)}`,
    staticMetadata: {
      services: ['cms'],
    },
  },
  '/meta-content-module/[metaContentModuleId]/study-activity/create': {
    href: ({ metaContentModuleId, ...query }) =>
      `/meta-content-module/${metaContentModuleId}/study-activity/create${qsStr(query)}`,
    staticMetadata: {
      services: ['cms'],
    },
  },
  '/meta-content-module/[metaContentModuleId]/block-activity/create': {
    href: ({ metaContentModuleId, ...query }) =>
      `/meta-content-module/${metaContentModuleId}/block-activity/create${qsStr(query)}`,
    staticMetadata: {
      services: ['cms'],
    },
  },
  '/curriculum/[curriculumId]/settings/dailycurriculum-setting/[dailyCurriculumId]': {
    href: (params) =>
      `/curriculum/${params.curriculumId}/settings/dailycurriculum-setting/${params.dailyCurriculumId}${qsStr(
        {
          mcmLimit: params.mcmLimit,
          mcmOffset: params.mcmOffset,
          mcmOrderBy: params.mcmOrderBy,
        },
      )}`,
    staticMetadata: {
      services: ['cms'],
    },
  },
  '/student-info': {
    href: (query) => `/student-info${qsStr(query)}`,
    staticMetadata: {
      services: ['lms'],
    },
  },
  '/chat': {
    href: (query) => `/chat`,
    staticMetadata: {
      services: ['lms'],
    },
  },
  '/chat/send-bulk-message': {
    href: (query) => `/chat/send-bulk-message`,
    staticMetadata: {
      services: ['lms'],
    },
  },
  '/curriculum/create': {
    href: (query) => '/curriculum/create',
    staticMetadata: {
      services: ['cms'],
    },
  },
  '/study-sync/[groupId]/[customStudyModuleId]/[customStudyUnitId]/learning/[customActivityId]': {
    href: ({ groupId, customStudyModuleId, customStudyUnitId, customActivityId, ...query }) =>
      `/study-sync/${groupId}/${customStudyModuleId}/${customStudyUnitId}/learning/${customActivityId}${qsStr(
        query,
      )}`,
    staticMetadata: {
      services: ['lms'],
    },
  },
  '/presentation/[groupId]/[customStudyModuleId]/[customStudyUnitId]/[customActivityId]': {
    href: ({ groupId, customStudyModuleId, customStudyUnitId, customActivityId, ...query }) =>
      `/presentation/${groupId}/${customStudyModuleId}/${customStudyUnitId}/${customActivityId}${qsStr(
        query,
      )}`,
    staticMetadata: {
      services: ['lms'],
    },
  },
  '/student-management': {
    href: () => `/student-management`,
    staticMetadata: {
      services: ['lms'],
    },
  },
  '/student-management/[groupId]/[studentId]': {
    href: (params) => `/student-management/${params.groupId}/${params.studentId}`,
    staticMetadata: {
      services: ['lms'],
    },
    dynamicMetadata: async ({ groupId, studentId }) => {
      const result = await apolloClient.query({
        query: gql`
          query DynamicMetadata_studentManagement($groupId: ID!, $studentId: ID!) {
            group(id: $groupId) {
              id
              name
            }
            user(id: $studentId) {
              id
              name
            }
          }
        `,
        variables: { groupId, studentId },
      });

      return {
        title: titleSplitter([
          result?.data?.group?.name ?? '',
          result?.data?.user?.name ?? '',
          '학생용 대시보드',
        ]),
      };
    },
  },
  '/student-management/[groupId]/[studentId]/detail/[curriculumId]': {
    href: (params) =>
      `/student-management/${params.groupId}/${params.studentId}/detail/${params.curriculumId}`,
    staticMetadata: {
      services: ['lms'],
    },
  },
  '/student-knowledge-status': {
    href: () => `/student-knowledge-status`,
    staticMetadata: {
      services: ['lms'],
    },
  },
  '/student-knowledge-status/[groupId]/[studentId]': {
    href: ({ groupId, studentId, ...query }) =>
      `/student-knowledge-status/${groupId}/${studentId}${qsStr(query)}`,
    staticMetadata: {
      services: ['lms'],
    },
  },
  '/class-monitor': {
    href: (query) => `/class-monitor${qsStr(query)}`,
    staticMetadata: {
      services: ['lms'],
      title: '실시간 수업',
    },
  },
  '/meta-content/[metaContentId]/chapter-setting/[metaChapterId]': {
    href: (params) => `/meta-content/${params.metaContentId}/chapter-setting/${params.metaChapterId}`,
    staticMetadata: {
      services: ['cms'],
    },
  },

  '/meta-content/[metaContentId]/chapter-setting/[metaChapterId]/[metaContentModuleId]': {
    href: (params) =>
      `/meta-content/${params.metaContentId}/chapter-setting/${params.metaChapterId}/${params.metaContentModuleId}`,
    staticMetadata: {
      services: ['cms'],
    },
  },
  '/meta-content/[metaContentId]/chapter-setting/[metaChapterId]/[metaContentModuleId]/[tabValue]': {
    href: (params) =>
      `/meta-content/${params.metaContentId}/chapter-setting/${params.metaChapterId}/${params.metaContentModuleId}/${params.tabValue}`,
    staticMetadata: {
      services: ['cms'],
    },
  },
  '/group/[groupId]/board': {
    href: ({ groupId }) => `/group/${groupId}/board`,
    staticMetadata: {
      services: [],
    },
  },
  '/group/[groupId]/board/create': {
    href: ({ groupId }) => `/group/${groupId}/board/create`,
    staticMetadata: {
      services: [],
    },
  },
  '/group/[groupId]/board/[postId]/edit': {
    href: ({ groupId, postId }) => `/group/${groupId}/board/${postId}/edit`,
    staticMetadata: {
      services: [],
    },
  },
  '/group/[groupId]/board/[postId]': {
    href: ({ groupId, postId }) => `/group/${groupId}/board/${postId}`,
    staticMetadata: {
      services: [],
    },
  },
  '/group/[groupId]/class-monitor': {
    href: ({ groupId }) => `/group/${groupId}/class-monitor`,
    staticMetadata: {
      services: ['lms'],
    },
  },
  '/group/[groupId]/class-monitor/[csmId]/learning/[learningActivityId]': {
    href: ({ groupId, csmId, learningActivityId }) =>
      `/group/${groupId}/class-monitor/${csmId}/learning/${learningActivityId}`,
    staticMetadata: {
      services: ['lms'],
    },
  },
  '/group/[groupId]/class-monitor/[csmId]/learning-root/[learningActivityId]': {
    href: ({ groupId, csmId, learningActivityId, modumHandwriting, modumId }) =>
      `/group/${groupId}/class-monitor/${csmId}/learning-root/${learningActivityId}${qsStr({
        modumHandwriting,
        modumId,
      })}`,
    staticMetadata: {
      services: ['lms'],
    },
  },
  '/group/[groupId]/class-monitor/[csmId]/summary/[suiId]': {
    href: ({ groupId, csmId, suiId }) => `/group/${groupId}/class-monitor/${csmId}/summary/${suiId}`,
    staticMetadata: {
      services: ['lms'],
    },
  },
  '/group/[groupId]/class-monitor/[csmId]/summary/[suiId]/unit-overview': {
    href: ({ groupId, csmId, suiId }) =>
      `/group/${groupId}/class-monitor/${csmId}/summary/${suiId}/unit-overview`,
    staticMetadata: {
      services: ['lms'],
    },
  },
  '/group/[groupId]/class-monitor/redirect/learning/[customActivityId]': {
    href: ({ groupId, customActivityId }) =>
      `/group/${groupId}/class-monitor/redirect/learning/${customActivityId}`,
    staticMetadata: {
      services: ['lms'],
    },
  },
  '/group/[groupId]/class-monitor/redirect/summary/[csuId]/[studentId]': {
    href: ({ groupId, csuId, studentId }) =>
      `/group/${groupId}/class-monitor/redirect/summary/${csuId}/${studentId}`,
    staticMetadata: {
      services: ['lms'],
    },
  },
  '/group/[groupId]/dashboard': {
    href: ({ groupId }) => `/group/${groupId}/dashboard`,
    staticMetadata: {
      services: ['lms'],
    },
  },
  '/group/[groupId]/chat-monitor': {
    href: ({ groupId }) => `/group/${groupId}/chat-monitor`,
    staticMetadata: {
      services: ['lms'],
    },
  },
  '/chat-attachment/[channelId]': {
    href: ({ channelId, imageFileId, bundleMessageId }) =>
      `/chat-attachment/${channelId}${qsStr({ imageFileId, bundleMessageId })}`,
    staticMetadata: {
      services: ['lms', 'b2c'],
    },
  },
  '/group/[groupId]/calendar': {
    href: ({ groupId }) => `/group/${groupId}/calendar`,
    staticMetadata: {
      services: ['lms'],
    },
  },

  '/group/[groupId]/learning-plan-feed': {
    href: ({ groupId }) => `/group/${groupId}/learning-plan-feed`,
    staticMetadata: {
      services: ['lms'],
    },
  },
  '/group/[groupId]/learning-plan-feed/[studentId]': {
    href: ({ groupId, studentId }) => `/group/${groupId}/learning-plan-feed/${studentId}`,
    staticMetadata: {
      services: ['lms'],
    },
  },
  '/group/[groupId]/learning-plan-feed/[studentId]/[learningPlanId]/plan-management': {
    href: ({ groupId, studentId, learningPlanId }) =>
      `/group/${groupId}/learning-plan-feed/${studentId}/${learningPlanId}/plan-management`,
    staticMetadata: {
      services: ['lms'],
    },
  },
  '/group/[groupId]/learning-plan-feed/[studentId]/[learningPlanId]/custom-knowledge-map': {
    href: ({ groupId, studentId, learningPlanId }) =>
      `/group/${groupId}/learning-plan-feed/${studentId}/${learningPlanId}/custom-knowledge-map`,
    staticMetadata: {
      services: ['lms'],
    },
  },

  '/group/[groupId]/student-management': {
    href: ({ groupId }) => `/group/${groupId}/student-management`,
    staticMetadata: {
      services: ['lms'],
    },
  },
  '/group/[groupId]/student-management/[studentId]': {
    href: (params) => `/group/${params.groupId}/student-management/${params.studentId}`,
    staticMetadata: {
      services: ['lms'],
    },
    dynamicMetadata: async ({ groupId, studentId }) => {
      const result = await apolloClient.query({
        query: gql`
          query DynamicMetadata_GroupStudentManagement($groupId: ID!, $studentId: ID!) {
            group(id: $groupId) {
              id
              name
            }
            user(id: $studentId) {
              id
              name
            }
          }
        `,
        variables: { groupId, studentId },
      });

      return {
        title: titleSplitter([
          result?.data?.group?.name ?? '',
          result?.data?.user?.name ?? '',
          '학생용 대시보드',
        ]),
      };
    },
  },
  '/group/[groupId]/student-management/[studentId]/detail/[curriculumId]': {
    href: (params) =>
      `/group/${params.groupId}/student-management/${params.studentId}/detail/${params.curriculumId}`,
    staticMetadata: {
      services: ['lms'],
    },
  },
  '/group/[groupId]/study-report/[studentId]': {
    href: (params) => `/group/${params.groupId}/study-report/${params.studentId}`,
    staticMetadata: {
      services: ['lms'],
      title: '커리큘럼 대시보드',
    },
  },
  '/group/[groupId]/study-report/all': {
    href: (params) => `/group/${params.groupId}/study-report/all`,
    staticMetadata: {
      services: ['lms'],
      title: '커리큘럼 대시보드',
    },
  },
  '/group/[groupId]/study-report/all/[customCurriculumId]': {
    href: (params) => `/group/${params.groupId}/study-report/all/${params.customCurriculumId}`,
    staticMetadata: {
      services: ['lms'],
      title: '커리큘럼 대시보드',
    },
  },

  '/group/[groupId]/study-report/[studentId]/[customCurriculumId]': {
    href: (params) =>
      `/group/${params.groupId}/study-report/${params.studentId}/${params.customCurriculumId}`,
    staticMetadata: {
      services: ['lms'],
      title: '커리큘럼 대시보드',
    },
  },
  '/group/[groupId]/student-study-report/[studentId]': {
    href: (params) => `/group/${params.groupId}/student-study-report/${params.studentId}`,
    staticMetadata: {
      services: ['b2c'],
      title: '커리큘럼 대시보드',
    },
  },
  '/group/[groupId]/student-study-report/my-report': {
    href: (params) => `/group/${params.groupId}/student-study-report/my-report`,
    staticMetadata: {
      services: ['b2c'],
      title: '커리큘럼 대시보드',
    },
  },
  '/group/[groupId]/student-study-report/[studentId]/[customCurriculumId]': {
    href: (params) =>
      `/group/${params.groupId}/student-study-report/${params.studentId}/${params.customCurriculumId}`,
    staticMetadata: {
      services: ['b2c'],
      title: '커리큘럼 대시보드',
    },
  },
  '/group/[groupId]/today-mission': {
    href: ({ groupId, todayMissionDate }) => `/group/${groupId}/today-mission${qsStr({ todayMissionDate })}`,
    staticMetadata: {
      services: ['b2c'],
    },
  },
  '/group/[groupId]/my-learning-status': {
    href: ({ groupId }) => `/group/${groupId}/my-learning-status`,
    staticMetadata: {
      services: ['b2c'],
    },
  },
  '/group/[groupId]/my-learning-status/[curriculumId]': {
    href: ({ groupId, curriculumId }) => `/my-learning-status/${groupId}/detail/${curriculumId}`,
    staticMetadata: {
      services: ['b2c'],
    },
  },
  '/group/[groupId]/regular-class': {
    href: ({ groupId }) => `/group/${groupId}/regular-class`,
    staticMetadata: {
      services: ['b2c'],
    },
  },
  '/group/[groupId]/regular-class/[smiId]/learning/[learningActivityId]': {
    href: ({ groupId, smiId, learningActivityId, chatbot }) => {
      const courseId =
        typeof window !== 'undefined'
          ? new URLSearchParams(window.location.search).get('courseId')
          : null;
      return `/group/${groupId}/regular-class/${smiId}/learning/${learningActivityId}${qsStr({ chatbot, ...(courseId ? { courseId } : {}) })}`;
    },
    staticMetadata: {
      services: ['b2c'],
    },
  },
  '/group/[groupId]/regular-class/[smiId]/summary/[suiId]': {
    href: ({ groupId, smiId, suiId }) => {
      const courseId =
        typeof window !== 'undefined'
          ? new URLSearchParams(window.location.search).get('courseId')
          : null;
      return `/group/${groupId}/regular-class/${smiId}/summary/${suiId}${courseId ? `?courseId=${encodeURIComponent(courseId)}` : ''}`;
    },
    staticMetadata: {
      services: ['b2c'],
    },
    dynamicMetadata: async ({ smiId, suiId }) => {
      const result = await apolloClient.query({
        query: gql`
          query RegularClassSummaryPageMetadata($suiId: ID!, $smiId: ID!) {
            studyModuleInstanceById(id: $smiId) {
              id
              title
            }
            studyUnitInstance(id: $suiId) {
              id
              title
            }
          }
        `,
        variables: { suiId, smiId },
      });

      return {
        title: titleSplitter([
          '학습 요약',
          result?.data?.studyUnitInstance?.title ?? '',
          result?.data?.studyModuleInstanceById?.title ?? '',
        ]),
      };
    },
  },
  '/group/[groupId]/assignments': {
    href: ({ groupId, limit, offset }) =>
      `/group/${groupId}/assignments${qsStr({
        limit,
        offset,
      })}`,
    staticMetadata: {
      services: ['lms'],
      title: '과제 목록',
    },
  },
  '/group/[groupId]/assignments/[assignmentId]': {
    href: ({ groupId, assignmentId }) => `/group/${groupId}/assignments/${assignmentId}`,
    staticMetadata: {
      services: ['lms'],
      title: '과제 상세',
    },
  },
  '/group/[groupId]/my-assignments': {
    href: ({ groupId, offset, limit }) =>
      `/group/${groupId}/assignments${qsStr({
        limit,
        offset,
      })}`,
    staticMetadata: {
      services: ['b2c'],
      title: '과제 목록',
    },
  },
  '/group/[groupId]/assignment-detail/[smiId]/learning/[learningActivityId]/[kind]': {
    href: ({ groupId, smiId, learningActivityId, kind }) =>
      `/group/${groupId}/assignment-detail/${smiId}/${kind}/learning/${learningActivityId}`,
    staticMetadata: {
      services: ['b2c'],
    },
  },
  '/group/[groupId]/assignment-detail/[smiId]/[kind]/summary/[suiId]': {
    href: ({ groupId, smiId, suiId, kind }) =>
      `/group/${groupId}/assignment-detail/${smiId}/${kind}/summary/${suiId}`,
    staticMetadata: {
      services: ['b2c'],
    },
  },
  '/group/[groupId]/self-study': {
    href: ({ groupId }) => `/group/${groupId}/self-study`,
    staticMetadata: {
      services: ['b2c'],
      title: '자습 목록',
    },
  },
  '/group/[groupId]/incorrect-answer-note': {
    href: ({ groupId }) => `/group/${groupId}/incorrect-answer-note`,
    staticMetadata: {
      services: ['b2c'],
      title: '오답 노트',
    },
  },
  '/study-analytics': {
    href: () => `/study-analytics`,
    staticMetadata: {
      services: ['lms'],
      title: '학습 분석',
    },
  },
  '/login-fail': {
    href: () => `/login-fail`,
    staticMetadata: {
      services: ['hub'],
      title: '로그인 실패',
    },
  },
  '/students': {
    href: () => `/students`,
    staticMetadata: {
      services: ['hub'],
      title: '학생 정보',
    },
  },
  '/students/[studentId]': {
    href: (params: { studentId: string }) => `/students/${params.studentId}`,
    staticMetadata: {
      services: ['hub'],
      title: '학생 정보 상세',
    },
  },
  '/students/[studentId]/courses': {
    href: (params: { studentId: string }) => `/students/${params.studentId}/courses`,
    staticMetadata: {
      services: ['hub'],
      title: '개강 정보',
    },
  },
  '/students/[studentId]/learning': {
    href: (params: { studentId: string }) => `/students/${params.studentId}/learning`,
    staticMetadata: {
      services: ['hub'],
      title: '학습 정보',
    },
  },
  '/students/[studentId]/payment': {
    href: (params: { studentId: string }) => `/students/${params.studentId}/payment`,
    staticMetadata: {
      services: ['hub'],
      title: '결제 정보',
    },
  },
  '/students/[studentId]/report': {
    href: (params: { studentId: string }) => `/students/${params.studentId}/report`,
    staticMetadata: {
      services: ['hub'],
      title: '학습 리포트',
    },
  },
  '/students/[studentId]/records': {
    href: (params: { studentId: string }) => `/students/${params.studentId}/records`,
    staticMetadata: {
      services: ['hub'],
      title: '학생 기록',
    },
  },
  '/students/[studentId]/records/[studentRecordId]': {
    href: (params: { studentId: string; studentRecordId: string }) =>
      `/students/${params.studentId}/records/${params.studentRecordId}`,
    staticMetadata: {
      services: ['hub'],
      title: '학생 기록 상세',
    },
  },
  '/students/[studentId]/records/create': {
    href: (params: { studentId: string }) => `/students/${params.studentId}/records/create`,
    staticMetadata: {
      services: ['hub'],
      title: '학생 기록 작성',
    },
  },
  '/students/[studentId]/records/[studentRecordId]/edit': {
    href: (params: { studentId: string; studentRecordId: string }) =>
      `/students/${params.studentId}/records/${params.studentRecordId}/edit`,
    staticMetadata: {
      services: ['hub'],
      title: '학생 기록 수정',
    },
  },
  '/purchase-history': {
    href: (params) =>
      `/purchase-history${qsStr({
        offset: params.offset,
        limit: params.limit,
        searchFilter: params.searchFilter,
      })}`,
    staticMetadata: {
      services: ['hub'],
      title: '결제 내역',
    },
  },
  '/purchase-history/[purchaseId]': {
    href: ({ purchaseId }) => `/purchase-history/${purchaseId}`,
    staticMetadata: {
      services: ['hub'],
      title: '결제 정보 상세',
    },
  },
  '/purchase-history/[purchaseId]/sales-counseling-record/create': {
    href: ({ purchaseId }) => `/purchase-history/${purchaseId}/sales-counseling-record/create`,
    staticMetadata: {
      services: ['hub'],
      title: '판매 상담 기록 작성',
    },
  },
  '/purchase-history/[purchaseId]/sales-counseling-record/[salesCounselingRecordId]': {
    href: ({ purchaseId, salesCounselingRecordId }) =>
      `/purchase-history/${purchaseId}/sales-counseling-record/${salesCounselingRecordId}`,
    staticMetadata: {
      services: ['hub'],
      title: '판매 상담 기록 상세',
    },
  },
  '/purchase-history/[purchaseId]/sales-counseling-record/[salesCounselingRecordId]/edit': {
    href: ({ purchaseId, salesCounselingRecordId }) =>
      `/purchase-history/${purchaseId}/sales-counseling-record/${salesCounselingRecordId}/edit`,
    staticMetadata: {
      services: ['hub'],
      title: '판매 상담 기록 수정',
    },
  },
  '/group/[groupId]/group-attendance': {
    href: ({ groupId, ...query }) => `/group/${groupId}/group-attendance${qsStr(query)}`,
    staticMetadata: {
      services: ['lms'],
      title: '출결 관리',
    },
  },
  '/mildang-ai-logs/[studentId]': {
    href: ({ studentId, ...query }) => `/mildang-ai-logs/${studentId}/${qsStr(query)}`,
    staticMetadata: {
      services: ['b2c'],
      title: '밀당AI Logs',
    },
  },

  '/school-managed': {
    href: () => `/school-managed`,
    staticMetadata: {
      title: '학교 관리',
    },
  },
  '/[organizationId]/[groupId]/daily-dashboard': {
    href: ({ organizationId, groupId }) => `/${organizationId}/${groupId}/daily-dashboard`,
    staticMetadata: {
      title: '일일 학급 관리',
    },
  },
  '/[organizationId]/[groupId]/study-report/all': {
    href: ({ organizationId, groupId }) => `/${organizationId}/${groupId}/study-report/all`,
    staticMetadata: {
      title: '학교 학습 보고서',
    },
  },
  '/[organizationId]/[groupId]/study-report/all/[customCurriculumId]': {
    href: ({ organizationId, groupId, customCurriculumId }) =>
      `/${organizationId}/${groupId}/study-report/all/${customCurriculumId}`,
    staticMetadata: {
      title: '학교 학습 보고서',
    },
  },
  '/[organizationId]/[groupId]/study-report/[studentId]/[customCurriculumId]': {
    href: ({ organizationId, groupId, studentId, customCurriculumId }) =>
      `/${organizationId}/${groupId}/study-report/${studentId}/${customCurriculumId}`,
    staticMetadata: {
      title: '학생 학습 보고서',
    },
  },
  '/[organizationId]/[groupId]/class-plan': {
    href: ({ organizationId, groupId }) => `/${organizationId}/${groupId}/class-plan`,
    staticMetadata: {
      title: '진도 관리',
    },
  },
  '/group/[groupId]/homework-board': {
    href: ({ groupId }) => `/group/${groupId}/homework-board`,
    staticMetadata: {
      services: [],
    },
  },
  '/group/[groupId]/homework-board/create': {
    href: ({ groupId }) => `/group/${groupId}/homework-board/create`,
    staticMetadata: {
      services: [],
    },
  },
  '/group/[groupId]/homework-board/[postId]': {
    href: ({ groupId, postId }) => `/group/${groupId}/homework-board/${postId}`,
    staticMetadata: {
      services: [],
    },
  },
  '/group/[groupId]/homework-board/[postId]/edit': {
    href: ({ groupId, postId }) => `/group/${groupId}/homework-board/${postId}/edit`,
    staticMetadata: {
      services: [],
    },
  },
  '/group/[groupId]/classroom-plan': {
    href: ({ groupId }) => `/group/${groupId}/classroom-plan`,
    staticMetadata: {
      services: ['lms'],
    },
  },
  // TODO: 학교 관리 페이지 검수 후 변경
  '/class-managed': {
    href: () => `/class-managed`,
    staticMetadata: {
      title: '학급 관리',
    },
  },
  '/[organizationId]/[groupId]/setting': {
    href: ({ organizationId, groupId }) => `/${organizationId}/${groupId}/setting`,
    staticMetadata: {
      title: '설정',
    },
  },
});

routePathConfig.setPathPrefix((path, params, staticMetadata) => {
  if (isProject('mildang')) {
    return '';
  }
  if (isProject('schoolpt') || isProject('educore')) {
    if (staticMetadata?.services?.includes('cms')) {
      return '/cms';
    }
    if (staticMetadata?.services?.includes('lms')) {
      return '/lms';
    }
    if (staticMetadata?.services?.includes('crm')) {
      return '/crm';
    }
    if (staticMetadata?.services?.includes('b2c')) {
      return '/s';
    }
  }

  return '';
});

export const routePath = routePathConfig;
